import { useEffect, useCallback, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ChevronLeft } from 'lucide-react';
import { useStore } from '@shared/hooks/useStore';
import IconImages from '@shared/assets/images/icons';
import ReaderTabBar from './components/ReaderTabBar';
import ReaderSidebar from './components/ReaderSidebar';
import PDFPageViewer from './components/PDFPageViewer';
import ReaderPagination from './components/ReaderPagination';
import ReaderToolbar from './components/ReaderToolbar';
import ReaderSearchSidebar from './components/ReaderSearchSidebar';
import ReaderBookmarkNoteSidebar from './components/ReaderBookmarkNoteSidebar';
import ReaderNoteModal from './components/ReaderNoteModal';
import ReaderTabsModal from './components/ReaderTabsModal';
import { toastify } from '@shared/utils/toastify';
import { flowResult } from 'mobx';
import { ROUTES } from '@shared/configs/constants';
import NotFoundPage from '@user/pages/not-found';
import { TYPE_SEARCH, TypeSearch } from '@shared/types/enums';

const MAX_MULTIPLIER = 3;
const MIN_MULTIPLIER = 0.5;
const MULTIPLIER_STEP = 0.25;

const ReaderPage = observer(() => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { readerStore, publicationStore, cartStore } = useStore();

    const [baseZoom, setBaseZoom] = useState(0);
    const [multiplier, setMultiplier] = useState(1);
    const zoom = Math.round(baseZoom * multiplier);

    const [showTabsModal, setShowTabsModal] = useState(false);
    const [initialSearchApplied, setInitialSearchApplied] = useState(false);

    const [firstLoad, setFirstLoad] = useState(true);

    const isPreview = location.pathname.startsWith('/preview');

    useEffect(() => {
        if (!id) return;

        const loadPublication = async () => {
            try {
                await publicationStore.fetchPublicationDetail(id);
            } finally {
                if (firstLoad) {
                    setFirstLoad(false);
                }
            }
            const publication = publicationStore.publicationDetail;

            if (publication) {
                if (!isPreview && !publication.owned) {
                    return;
                }
                readerStore.setSessionMode(isPreview ? 'preview' : 'reader');
                const existingBook = readerStore.openBooks.find(
                    b => b.publication.id === id
                );
                readerStore.addBook({
                    ...publication,
                    isPreview
                });

                const pageToFetch = existingBook ? existingBook.currentPage : 1;
                readerStore.fetchPage(id, pageToFetch);
            }
        };

        loadPublication();
    }, [id, readerStore, publicationStore]);

    // Auto-open search sidebar when search param is provided
    useEffect(() => {
        const searchQuery = searchParams.get('search');
        const searchTypeParam = searchParams.get('searchType') as TypeSearch | null;
        const searchType = searchTypeParam === TYPE_SEARCH.AND ? TYPE_SEARCH.AND : TYPE_SEARCH.OR;
        if (searchQuery && !initialSearchApplied && !readerStore.isLoadingPage && readerStore.getActiveBook()) {
            readerStore.toggleSearch();
            readerStore.search(searchQuery, false, searchType);
            setInitialSearchApplied(true);
        }
    }, [searchParams, readerStore, initialSearchApplied, readerStore.isLoadingPage]);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        const activeBook = readerStore.getActiveBook();
        if (!activeBook || readerStore.isSearchOpen || readerStore.isBookmarkNoteOpen) return;

        if (e.key === 'ArrowLeft' && activeBook.currentPage > 1) {
            readerStore.fetchPage(
                activeBook.publication.id,
                activeBook.currentPage - 1
            );
        } else if (e.key === 'ArrowRight' && activeBook.currentPage < (activeBook.publication.isPreview ? activeBook.publication.previewPageCount : activeBook.publication.totalNumberPages)) {
            readerStore.fetchPage(
                activeBook.publication.id,
                activeBook.currentPage + 1
            );
        } else if (e.key === '+' || e.key === '=') {
            handleZoomIn();
        } else if (e.key === '-') {
            handleZoomOut();
        }
    }, [readerStore, zoom]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const handleTabClick = (publicationId: string) => {
        const book = readerStore.openBooks.find(b => b.publication.id === publicationId);
        if (book) {
            readerStore.closeSearch();
            readerStore.closeBookmarkNote();

            readerStore.fetchPage(publicationId, book.currentPage);
            const route = book.publication.isPreview ? '/preview' : '/reader';
            navigate(`${route}/${publicationId}`, { replace: true });
        }
    };

    const handleTabClose = (publicationId: string) => {
        if (readerStore.openBooks.length === 0) {
            navigate('/');
        } else if (readerStore.activeBookId) {
            const nextBook = readerStore.getActiveBook();
            if (nextBook) {
                const route = nextBook.publication.isPreview ? '/preview' : '/reader';
                navigate(`${route}/${readerStore.activeBookId}`, { replace: true });
            }
        }
    };

    const handleBuyNow = async () => {
        const activeBook = readerStore.getActiveBook();
        const publication = activeBook?.publication;
        if (!publication) {
            toastify('error', '商品情報が取得できませんでした');
            return;
        }

        const res = await flowResult(cartStore.getPreCheckoutCart([publication.id.toString()]));
        if (res) {
            navigate(ROUTES.checkout.href, { state: { cartItems: res } });
        }
    };

    const handleGoPublication = () => navigate('/profile/my-library/publications');

    const handleZoomIn = () => {
        setMultiplier(prev => Math.min(prev + MULTIPLIER_STEP, MAX_MULTIPLIER));
    };

    const handleZoomOut = () => {
        setMultiplier(prev => Math.max(prev - MULTIPLIER_STEP, MIN_MULTIPLIER));
    };

    const handlePageLoad = useCallback((zoomValue: number) => {
        setBaseZoom(zoomValue);
        setMultiplier(1);
    }, []);

    if (!publicationStore.publicationDetail && firstLoad) {
        return (
            <></>
        );
    }

    if (!publicationStore.publicationDetail || (!isPreview && !publicationStore.publicationDetail.owned)) {
        return (
            <NotFoundPage />
        );
    }

    return (
        <div className='h-[100dvh] flex flex-col bg-[#29292B] overflow-hidden text-break'>
            <div className='flex-shrink-0'>
                <div className='flex items-center bg-[#29292B] px-3 py-3 sm:px-5 sm:py-1.5 gap-2 sm:gap-8 shadow-[0_8px_32px_0_rgba(0,0,0,0.5)] z-20 relative'>
                    <div className='flex items-center gap-1 sm:gap-2'>
                        <button
                            onClick={handleGoPublication}
                            className='flex items-center flex-shrink-0 text-[#F2F2F2] hover:text-primary transition-colors'
                        >
                            <ChevronLeft size={24} />
                        </button>
                    </div>

                    <div className='flex-1 overflow-hidden'>
                        {/* Mobile: Gallery icon */}
                        <button
                            onClick={() => setShowTabsModal(true)}
                            className='sm:hidden p-1.5 rounded transition-colors text-white hover:bg-[#FFFFFF1A]'
                            title='タブ'
                        >
                            <img src={IconImages.galleryIc} alt='tabs' className='w-5 h-5' />
                        </button>
                        {/* Desktop: Tab bar */}
                        <div className='hidden sm:block'>
                            <ReaderTabBar
                                onTabClick={handleTabClick}
                                onTabClose={handleTabClose}
                            />
                        </div>
                    </div>

                    <div className='flex-shrink-0 sm:px-2'>
                        <ReaderToolbar
                            zoom={zoom}
                            multiplier={multiplier}
                            onZoomIn={handleZoomIn}
                            onZoomOut={handleZoomOut}
                            isPreview={isPreview}
                            minMultiplier={MIN_MULTIPLIER}
                            maxMultiplier={MAX_MULTIPLIER}
                        />
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className={`flex-1 flex overflow-hidden relative ${readerStore.isSearchOpen || readerStore.isBookmarkNoteOpen ? 'z-30' : 'z-10'}`}>
                {/* Sidebar */}
                {
                    isPreview && (
                        <div className='bg-[#29292B] hidden lg:block inset-y-0 left-0 z-30 lg:static shadow-2xl lg:shadow-none flex-shrink-0'>
                            <div className='h-full overflow-y-auto w-[280px] sm:w-[320px]'>
                                <ReaderSidebar onBuyNow={handleBuyNow} />
                            </div>
                        </div>
                    )
                }

                {/* PDF Viewer Area */}
                <div className='flex-1 flex flex-col min-w-0 min-h-0 relative'>
                    {/* PDF Viewer */}
                    <PDFPageViewer zoom={zoom} onPageLoad={handlePageLoad} />

                    {/* Pagination */}
                    <ReaderPagination />
                </div>

                {
                    readerStore.isSearchOpen && (
                        <div className='bg-[#29292B] fixed inset-0 h-[100dvh] sm:h-full sm:absolute sm:inset-y-0 sm:right-0 sm:left-auto z-40 lg:static shadow-2xl lg:shadow-none'>
                            <div className='h-full overflow-y-auto w-full sm:w-[320px]'>
                                <ReaderSearchSidebar />
                            </div>
                        </div>
                    )
                }

                {
                    !isPreview && readerStore.isBookmarkNoteOpen && (
                        <div className='bg-[#29292B] fixed inset-0 h-[100dvh] sm:h-full sm:absolute sm:inset-y-0 sm:right-0 sm:left-auto z-40 lg:static shadow-2xl lg:shadow-none'>
                            <div className='h-full overflow-y-auto w-full sm:w-[320px]'>
                                <ReaderBookmarkNoteSidebar />
                            </div>
                        </div>
                    )
                }
            </div>

            {!isPreview && <ReaderNoteModal />}
            <ReaderTabsModal
                isOpen={showTabsModal}
                onClose={() => setShowTabsModal(false)}
                onTabClick={handleTabClick}
                onTabClose={handleTabClose}
            />
        </div>
    );
});

export default ReaderPage;
